import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

/**
 * Date de dernière ouverture du Feed, stockée sur le compte
 * (`profiles.feed_seen_at`, migration 0272) : le badge « nouveautés » est le
 * même sur tous les appareils.
 */

// Colonne et RPC postérieures aux types générés → client générique.
const db = supabase as unknown as SupabaseClient;

export const feedSeenKey = (userId: string | undefined) => ['feed-seen-at', userId] as const;

export function useFeedSeenAt() {
  const { user } = useAuth();
  return useQuery({
    queryKey: feedSeenKey(user?.id),
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await db
        .from('profiles')
        .select('feed_seen_at')
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as { feed_seen_at: string | null } | null)?.feed_seen_at ?? null;
    },
  });
}

/**
 * À l'ouverture de la page Feed : fige la date PRÉCÉDENTE (pour marquer les
 * cartes nouvelles pendant toute la visite), puis enregistre « vu maintenant »
 * (le badge de nav retombe à 0). Renvoie `undefined` tant que la date n'est
 * pas connue — ne rien marquer comme nouveau d'ici là.
 */
export function useFeedVisit(): string | null | undefined {
  const { user } = useAuth();
  const qc = useQueryClient();
  const seen = useFeedSeenAt();
  const [seenAtAtOpen, setSeenAtAtOpen] = useState<string | null | undefined>(undefined);
  const markedRef = useRef(false);

  useEffect(() => {
    if (!user?.id || !seen.isSuccess || markedRef.current) return;
    markedRef.current = true;
    setSeenAtAtOpen(seen.data);
    void db
      .rpc('mark_feed_seen')
      .then(({ data, error }) => {
        if (error) throw error;
        qc.setQueryData(feedSeenKey(user.id), (data as string | null) ?? new Date().toISOString());
      })
      .then(undefined, (err: unknown) => console.error('[feed] mark_feed_seen failed', err));
  }, [user?.id, seen.isSuccess, seen.data, qc]);

  return seenAtAtOpen;
}
